import { SIZE } from '../board.js';
import { RuleError } from '../errors.js';
import { fnv1a } from '../hash.js';
import { DEFAULT_ICE_CONFIG, ICE_RULES_VERSION } from './config.js';
import { applyIceAction, newIceGame, otherSide, resignIce } from './game.js';
import { HOLE, LAKE_GENERATOR_VERSION, lakeFromHeights } from './lake.js';
import { fromIceNotation } from './notation.js';
import type { IceConfig, IceState, IceWinReason, Side } from './types.js';

export const ICE_RECORD_FORMAT = 1;

/** Where the lake came from: Lake #seed, or fixed thickness values (tests, tutorials). */
export type LakeOrigin = { kind: 'seed'; seed: number } | { kind: 'custom'; heights: number[][] };

/** Everything needed to replay a game on the ice. */
export interface IceRecord {
  format: number;
  rulesVersion: number;
  generatorVersion: number;
  lake: LakeOrigin;
  crackSeed: number;
  /** Only the options that differ from the defaults. */
  config: Partial<IceConfig>;
  moves: string[];
  result: { winner: Side; reason: IceWinReason } | null;
}

const CONFIG_KEYS: readonly (keyof IceConfig)[] = [
  'roundLimit',
  'gloryTarget',
  'crackEvery',
  'cracksPerGroan',
  'thawDelay',
];

/** A lake made with `lakeFromHeights` has no floes. */
function lakeOrigin(state: IceState): LakeOrigin {
  return state.lake.floes.length > 0
    ? { kind: 'seed', seed: state.lake.seed }
    : { kind: 'custom', heights: state.lake.heights.map((row) => [...row]) };
}

export function iceToRecord(state: IceState): IceRecord {
  const config: Partial<IceConfig> = {};
  for (const key of CONFIG_KEYS) {
    if (state.config[key] !== DEFAULT_ICE_CONFIG[key]) config[key] = state.config[key];
  }
  return {
    format: ICE_RECORD_FORMAT,
    rulesVersion: ICE_RULES_VERSION,
    generatorVersion: LAKE_GENERATOR_VERSION,
    lake: lakeOrigin(state),
    crackSeed: state.crackSeed,
    config,
    moves: [...state.moves],
    result:
      state.winner === null || state.winReason === null
        ? null
        : { winner: state.winner, reason: state.winReason },
  };
}

/** Thickness as 81 characters, rank 9 first: `0`–`3`, and `x` for a hole. */
function formatHeights(heights: number[][]): string {
  return heights.map((row) => row.map((h) => (h === HOLE ? 'x' : String(h))).join('')).join('');
}

function parseHeights(text: string): number[][] {
  if (!/^[0-3x]{81}$/.test(text)) bad(`Unreadable lake "${text}"`);
  return Array.from({ length: SIZE }, (_, r) =>
    [...text.slice(r * SIZE, r * SIZE + SIZE)].map((ch) => (ch === 'x' ? HOLE : Number(ch))),
  );
}

/**
 * Compact text record, for example:
 *
 * ```
 * [HalvardIce "1"]
 * [Rules "2"]
 * [Generator "1"]
 * [Lake "482193"]
 * [Cracks "77"]
 * [Result "rime held"]
 *
 * +Sp d2; +Hu e8; ...; Sk e2-e4; Hu d2-d3>e4; pass
 * ```
 */
export function serializeIce(state: IceState): string {
  const record = iceToRecord(state);
  const lake =
    record.lake.kind === 'seed'
      ? String(record.lake.seed)
      : `custom:${formatHeights(record.lake.heights)}`;
  const lines = [
    `[HalvardIce "${record.format}"]`,
    `[Rules "${record.rulesVersion}"]`,
    `[Generator "${record.generatorVersion}"]`,
    `[Lake "${lake}"]`,
    `[Cracks "${record.crackSeed}"]`,
  ];
  const config = Object.entries(record.config).map(([k, v]) => `${k}=${String(v)}`);
  if (config.length > 0) lines.push(`[Config "${config.join(',')}"]`);
  if (record.result) lines.push(`[Result "${record.result.winner} ${record.result.reason}"]`);
  return `${lines.join('\n')}\n\n${record.moves.join('; ')}\n`;
}

export function deserializeIce(text: string): IceRecord {
  const tags = new Map<string, string>();
  const body: string[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (line === '') continue;
    const tag = /^\[(\w+) "([^"]*)"\]$/.exec(line);
    if (tag) tags.set(tag[1] ?? '', tag[2] ?? '');
    else body.push(line);
  }

  const format = parseInteger(required(tags, 'HalvardIce'));
  if (format !== ICE_RECORD_FORMAT) bad(`Unsupported record format ${format}`);
  const generatorVersion = parseInteger(required(tags, 'Generator'));
  if (generatorVersion !== LAKE_GENERATOR_VERSION) {
    bad(`Lake generator ${generatorVersion} is not supported`);
  }

  const lakeTag = required(tags, 'Lake');
  const lake: LakeOrigin = lakeTag.startsWith('custom:')
    ? { kind: 'custom', heights: parseHeights(lakeTag.slice('custom:'.length)) }
    : { kind: 'seed', seed: parseInteger(lakeTag) };

  const config: Partial<IceConfig> = {};
  const configTag = tags.get('Config');
  if (configTag) {
    for (const pair of configTag.split(',')) {
      const [key = '', value = ''] = pair.split('=');
      const known = CONFIG_KEYS.find((k) => k === key);
      if (!known) bad(`Unknown config option "${key}"`);
      config[known] = parseInteger(value);
    }
  }

  let result: IceRecord['result'] = null;
  const resultTag = tags.get('Result');
  if (resultTag !== undefined) {
    const match = /^(brand|rime) (glory|held|sunk|resign)$/.exec(resultTag);
    if (!match) bad(`Unreadable result "${resultTag}"`);
    result = { winner: match[1] as Side, reason: match[2] as IceWinReason };
  }

  return {
    format,
    rulesVersion: parseInteger(required(tags, 'Rules')),
    generatorVersion,
    lake,
    crackSeed: parseInteger(required(tags, 'Cracks')),
    config,
    moves: body
      .join(' ')
      .split(';')
      .map((m) => m.trim())
      .filter((m) => m !== ''),
    result,
  };
}

/** Plays a record from the start. Throws `BAD_RECORD` if a move or the result doesn't match. */
export function replayIce(record: IceRecord): IceState {
  let state = newIceGame({
    ...(record.lake.kind === 'seed'
      ? { seed: record.lake.seed }
      : { lake: lakeFromHeights(record.lake.heights) }),
    crackSeed: record.crackSeed,
    config: record.config,
  });
  record.moves.forEach((move, i) => {
    try {
      state = applyIceAction(state, fromIceNotation(state, move)).state;
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      bad(`Move ${i + 1} "${move}": ${reason}`);
    }
  });
  // A resignation isn't a move: the record's result says who gave up.
  if (record.result?.reason === 'resign' && state.phase !== 'over') {
    state = resignIce(state, otherSide(record.result.winner)).state;
  }
  const result = state.winner === null ? null : `${state.winner} ${state.winReason ?? ''}`;
  const expected = record.result && `${record.result.winner} ${record.result.reason}`;
  if (record.result !== null && result !== expected) {
    bad(`The record says "${expected ?? ''}" but the moves give "${result ?? 'no result'}"`);
  }
  return state;
}

/** Short fingerprint of the whole state, for desync checks between client and server. */
export function iceStateHash(state: IceState): string {
  const warriors = state.warriors
    .map((w) => `${w.id}@${w.r}${w.c}`)
    .sort()
    .join(',');
  const cracks = state.cracks
    .map((c) => `${c.r}${c.c}:${c.thawRound}`)
    .sort()
    .join(',');
  return fnv1a(
    [
      state.phase,
      state.turn,
      state.round,
      state.glory,
      state.winner ?? '-',
      formatHeights(state.heights),
      warriors,
      cracks,
    ].join('|'),
  );
}

function required(tags: Map<string, string>, name: string): string {
  const value = tags.get(name);
  if (value === undefined) bad(`Missing [${name}] tag`);
  return value;
}

function parseInteger(text: string): number {
  if (!/^-?\d+$/.test(text)) bad(`Not a whole number: "${text}"`);
  return Number(text);
}

function bad(message: string): never {
  throw new RuleError('BAD_RECORD', message);
}
